import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import ChevronDownSmall from 'assets/svg/ChevronDownSmall';
import type { CalendarEvent, CalendarRow, ChangeType, Frequency, Period } from '../../../../types/calendar';
import {
    ACTION_COLUMN_WIDTH,
    FIXED_COLUMNS_WIDTH_RATIO,
    MIN_DEFAULT_COLUMN_WIDTH,
    PERIOD_CELL_STYLE,
} from '../../../../config/calendarConstants';
import type { CalendarColumnConfig } from '../../../../config/calendarColumnsConfig';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { CHANGE_TYPE_CONFIG } from '../../../../config/changeTypeConfig';
import {
    TPO_CALENDAR_INTERVAL_BTN,
    tpoCalendarPeriodCellTestId,
    tpoCalendarPeriodColTestId,
    tpoRowActionsBtnTestId,
    tpoTableCellTestId,
    tpoTableColTestId,
    tpoTableExpandTestId,
} from '../../../../config/testIds';
import { CALENDAR_CELL_RENDERERS, renderKeepEventIndicator, renderPromoName } from '../../cellRenderers';
import { calculatePeriodColumnWidth, formatPeriodLabel } from '../../utils/periodLayout';
import PeriodCell from '../../components/PeriodCell';
import { isEmptyPlaceholderRow } from '../queries/useCalendarData';

const CHANGE_INDICATOR_WIDTH = 48;
const EMPTY_ROW_ACTION_TOOLTIP_FALLBACK = 'Add events to proceed';

// Distributes a pixel budget among columns based on their `flex` weights. Each column gets at least `MIN_DEFAULT_COLUMN_WIDTH` px.
function computeFlexWidths(columns: CalendarColumnConfig[], budget: number): Map<string, number> {
    const totalFlex = columns.reduce((sum, c) => sum + c.flex, 0);
    const widths = new Map<string, number>();
    for (const col of columns) {
        widths.set(col.key, Math.max(Math.floor(budget * (col.flex / totalFlex)), MIN_DEFAULT_COLUMN_WIDTH));
    }
    return widths;
}

export interface UseCalendarColumnsParams {
    periods: Period[];
    frequency: Frequency;
    containerWidth: number;
    expandedRowKeys: string[];
    setExpandedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
    onClickInterval: () => void;
    selectedColumnKeys: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    getColumnSortOrder: (dataIndex: string) => 'ascend' | 'descend' | null;
    getRowActionMenuItems: (record: CalendarRow) => MenuProps['items'];
    showChanges: boolean;
    onEditEvent: (record: CalendarRow) => void;
    onEditPromo: (record: CalendarRow) => void;
}

const isEditableRow = (record: CalendarRow): boolean => !record.children;

export function useCalendarColumns({
    periods,
    frequency,
    containerWidth,
    expandedRowKeys,
    setExpandedRowKeys,
    onClickInterval,
    selectedColumnKeys,
    getColumnSortOrder,
    getRowActionMenuItems,
    showChanges,
    onEditEvent,
    onEditPromo,
}: UseCalendarColumnsParams) {
    const { allDefaultColumns, labels, weekStartDay } = useTpoGenConfig();
    const emptyRowActionTooltip = labels.events.emptyRowActionTooltip ?? EMPTY_ROW_ACTION_TOOLTIP_FALLBACK;
    const keepIndicatorLabels = useMemo(
        () => ({
            keepDates: labels.keepFlags.keepDatesIndicatorTooltip,
            keepPromo: labels.keepFlags.keepPromoIndicatorTooltip,
            keepEvent: labels.keepFlags.keepEventIndicatorTooltip,
        }),
        [
            labels.keepFlags.keepDatesIndicatorTooltip,
            labels.keepFlags.keepPromoIndicatorTooltip,
            labels.keepFlags.keepEventIndicatorTooltip,
        ],
    );
    const selectedDefaultColumns = useMemo(
        () => allDefaultColumns.filter((col) => selectedColumnKeys.includes(col.key)),
        [allDefaultColumns, selectedColumnKeys],
    );

    const toggleExpandedRow = useCallback(
        (key: string) => {
            setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
        },
        [setExpandedRowKeys],
    );

    const defaultColumnsBudget = Math.floor(window.innerWidth * FIXED_COLUMNS_WIDTH_RATIO);
    const flexWidths = useMemo(
        () => computeFlexWidths(selectedDefaultColumns, defaultColumnsBudget),
        [selectedDefaultColumns, defaultColumnsBudget],
    );

    const visibleDefaultColumns = useMemo(
        () =>
            selectedDefaultColumns.map((col) => {
                const colWidth = flexWidths.get(col.key)!;
                const cellStyle = { maxWidth: colWidth } as React.CSSProperties;
                const headerCell = () => ({ 'data-testid': tpoTableColTestId('calendar', col.key) });
                const cellProps = (record: CalendarRow) => ({
                    style: cellStyle,
                    'data-testid': tpoTableCellTestId('calendar', record.key, col.key),
                });
                const withSort = col.sorter ? { sortOrder: getColumnSortOrder(col.dataIndex) } : {};
                const baseRender = CALENDAR_CELL_RENDERERS[col.key];

                if (col.key === 'event_name') {
                    return {
                        ...col,
                        ...withSort,
                        width: colWidth,
                        ellipsis: { showTitle: false },
                        onHeaderCell: headerCell,
                        onCell: (record: CalendarRow) =>
                            record.children
                                ? {
                                      ...cellProps(record),
                                      className: 'cursor-pointer',
                                      onClick: () => toggleExpandedRow(record.key),
                                  }
                                : cellProps(record),
                        title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                        render: (text: string, record: CalendarRow) => {
                            const isEditable = isEditableRow(record) && !!record.event_id;
                            const nameNode = (
                                <Tooltip title={text} showArrow={false}>
                                    <span
                                        data-testid={tpoTableCellTestId('calendar', record.key, 'event_name_link')}
                                        className={`truncate text-sm ${
                                            record.children ? 'font-semibold' : 'font-normal'
                                        } ${isEditable ? 'text-purpleFont cursor-pointer underline' : ''}`}
                                        onClick={(e) => {
                                            if (!isEditable) return;
                                            e.stopPropagation();
                                            onEditEvent(record);
                                        }}
                                    >
                                        {text}
                                    </span>
                                </Tooltip>
                            );

                            return (
                                <div className="flex items-center min-w-0">
                                    {record.children ? (
                                        <span
                                            data-testid={tpoTableExpandTestId('calendar', record.key)}
                                            data-state-expanded={
                                                expandedRowKeys.includes(record.key) ? 'true' : undefined
                                            }
                                            className="cursor-pointer mr-2 text-xs flex-shrink-0"
                                        >
                                            <ChevronDownSmall
                                                className={
                                                    expandedRowKeys.includes(record.key) ? 'rotate-180' : 'rotate-0'
                                                }
                                                size={12}
                                            />
                                        </span>
                                    ) : null}
                                    {renderKeepEventIndicator(
                                        record,
                                        keepIndicatorLabels.keepEvent,
                                        tpoTableCellTestId('calendar', record.key, col.key),
                                    )}
                                    {nameNode}
                                </div>
                            );
                        },
                    };
                }

                if (col.key === 'promo_name' || col.key === 'promotion_mechanic') {
                    return {
                        ...col,
                        ...withSort,
                        width: colWidth,
                        ellipsis: { showTitle: false },
                        onHeaderCell: headerCell,
                        onCell: cellProps,
                        title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                        render: (text: string, record: CalendarRow) => {
                            const isEditable = isEditableRow(record) && !!record.promo_id;
                            return (
                                <div
                                    data-testid={tpoTableCellTestId('calendar', record.key, `${col.key}_link`)}
                                    className={`min-w-0 overflow-hidden ${isEditable ? 'cursor-pointer' : ''}`}
                                    onClick={(e) => {
                                        if (!isEditable) return;
                                        e.stopPropagation();
                                        onEditPromo(record);
                                    }}
                                >
                                    {renderPromoName(
                                        text,
                                        record,
                                        isEditable,
                                        keepIndicatorLabels,
                                        tpoTableCellTestId('calendar', record.key, col.key),
                                    )}
                                </div>
                            );
                        },
                    };
                }

                return {
                    ...col,
                    ...withSort,
                    width: colWidth,
                    ellipsis: { showTitle: false },
                    onHeaderCell: headerCell,
                    onCell: cellProps,
                    title: <p className="text-sm font-semibold text-[#18181B]">{col.label}</p>,
                    render: baseRender,
                };
            }),
        [
            selectedDefaultColumns,
            flexWidths,
            expandedRowKeys,
            getColumnSortOrder,
            toggleExpandedRow,
            onEditEvent,
            onEditPromo,
            keepIndicatorLabels,
        ],
    );

    const changeIndicatorColumn = useMemo(
        () =>
            showChanges
                ? {
                      key: 'change_indicator',
                      title: '',
                      dataIndex: 'promo_lifecycle_status',
                      fixed: 'left' as const,
                      width: CHANGE_INDICATOR_WIDTH,
                      onHeaderCell: () => ({
                          'data-testid': tpoTableColTestId('calendar', 'change_indicator'),
                      }),
                      render: (changeType: ChangeType | null | undefined, record: CalendarRow) => {
                          if (record.children || !changeType) return null;
                          const config = CHANGE_TYPE_CONFIG[changeType];
                          if (!config) return null;
                          const Icon = config.icon;
                          return (
                              <div
                                  data-testid={tpoTableCellTestId('calendar', record.key, 'change_indicator')}
                                  data-state-change={changeType}
                                  className="flex items-center justify-center"
                              >
                                  <Icon size={16} />
                              </div>
                          );
                      },
                  }
                : null,
        [showChanges],
    );

    const fixedColumnsWidth = defaultColumnsBudget + (changeIndicatorColumn ? CHANGE_INDICATOR_WIDTH : 0);

    const periodColumns = useMemo(() => {
        if (!periods.length) return [];

        const cellWidth = calculatePeriodColumnWidth(periods.length, frequency, containerWidth, fixedColumnsWidth);

        return periods.map((period) => {
            // Every column is the same width but months span 28-31 days, so the scale has to
            // be derived per period. `period_end_date` is inclusive, hence the extra day.
            const spannedDays = dayjs(period.period_end_date).diff(dayjs(period.period_start_date), 'day') + 1;
            const pxPerDay = cellWidth / (spannedDays > 0 ? spannedDays : 7);

            return {
                key: period.period_start_date,
                title: (
                    <p className="text-sm text-[#18181B] truncate font-semibold">
                        {formatPeriodLabel(period.period_start_date, frequency)}
                    </p>
                ),
                dataIndex: period.period_start_date,
                width: cellWidth,
                onHeaderCell: () => ({
                    'data-testid': tpoCalendarPeriodColTestId(period.period_start_date),
                }),
                onCell: (record: CalendarRow) => ({
                    style: PERIOD_CELL_STYLE,
                    'data-testid': tpoCalendarPeriodCellTestId(record.key, period.period_start_date),
                }),
                ellipsis: true,
                render: (events: CalendarEvent[], record: CalendarRow) =>
                    record.children ? null : (
                        <PeriodCell
                            events={events ?? []}
                            frequency={frequency}
                            pxPerDay={pxPerDay}
                            periodStartDate={period.period_start_date}
                            cellWidth={cellWidth}
                            totalDays={spannedDays}
                            weekStartDay={weekStartDay}
                            testId={tpoCalendarPeriodCellTestId(record.key, period.period_start_date)}
                        />
                    ),
            };
        });
    }, [periods, frequency, containerWidth, fixedColumnsWidth, weekStartDay]);

    const actionColumn = useMemo(
        () => ({
            key: 'actions',
            title: (
                <MoreOutlined
                    data-testid={TPO_CALENDAR_INTERVAL_BTN}
                    style={{ fontSize: 16, cursor: 'pointer' }}
                    onClick={onClickInterval}
                />
            ),
            fixed: 'right' as const,
            width: ACTION_COLUMN_WIDTH,
            onHeaderCell: () => ({ 'data-testid': tpoTableColTestId('calendar', 'actions') }),
            render: (_: unknown, record: CalendarRow) => {
                if (record.children) return null;
                if (isEmptyPlaceholderRow(record)) {
                    return (
                        <div className="flex items-center justify-center">
                            <Tooltip title={emptyRowActionTooltip}>
                                <MoreOutlined
                                    data-testid={tpoRowActionsBtnTestId('calendar', record.key)}
                                    data-state-disabled="true"
                                    style={{ fontSize: 16, color: '#D4D4D8', cursor: 'not-allowed' }}
                                />
                            </Tooltip>
                        </div>
                    );
                }
                return (
                    <div className="flex items-center justify-center">
                        <Dropdown
                            menu={{ items: getRowActionMenuItems(record) }}
                            trigger={['click']}
                            overlayStyle={{ width: '10.9375rem', borderRadius: '0.5rem' }}
                        >
                            <MoreOutlined
                                data-testid={tpoRowActionsBtnTestId('calendar', record.key)}
                                style={{ fontSize: 16, cursor: 'pointer' }}
                            />
                        </Dropdown>
                    </div>
                );
            },
        }),
        [onClickInterval, getRowActionMenuItems, emptyRowActionTooltip],
    );

    return useMemo(
        () => [
            ...(changeIndicatorColumn ? [changeIndicatorColumn] : []),
            ...visibleDefaultColumns,
            ...periodColumns,
            actionColumn,
        ],
        [changeIndicatorColumn, visibleDefaultColumns, periodColumns, actionColumn],
    );
}
